// Sign an owner call (prepared by POST /api/vault/:asset/prepare) in the merchant's own browser wallet.
// EVM: window.ethereum (MetaMask & co.). TRON: TronLink (window.tronLink / window.tronWeb).
// The console never sees a private key or seed phrase: the wallet signs and broadcasts.
import type { PreparedCall } from "../api/types.ts";

type Eip1193 = { request: (args: { method: string; params?: unknown[] }) => Promise<unknown> };
type TronWebLike = {
  ready?: boolean;
  defaultAddress?: { base58?: string | false };
  transactionBuilder: {
    triggerSmartContract: (
      to: string, selector: string, options: { feeLimit: number; rawParameter: string }, params: unknown[], issuer: string,
    ) => Promise<{ result?: { result?: boolean }; transaction: unknown }>;
  };
  trx: {
    sign: (tx: unknown) => Promise<unknown>;
    sendRawTransaction: (signed: unknown) => Promise<{ result?: boolean; txid?: string; code?: string; message?: string; transaction?: { txID?: string } }>;
  };
};
type TronLinkLike = { ready?: boolean; tronWeb?: TronWebLike; request?: (args: { method: string }) => Promise<unknown> };

declare global {
  interface Window {
    ethereum?: Eip1193;
    tronWeb?: TronWebLike;
    tronLink?: TronLinkLike;
  }
}

export type WalletErrorKind = "no_wallet" | "no_account" | "wrong_account" | "rejected" | "wrong_network" | "insufficient_gas" | "failed";
export class WalletError extends Error {
  readonly kind: WalletErrorKind;
  readonly account?: string;
  constructor(kind: WalletErrorKind, message = "", account?: string) {
    super(message || kind);
    this.kind = kind;
    this.account = account;
  }
}

export const isTron = (chain: string) => chain === "tron";
export const hasWallet = (chain: string) => (isTron(chain) ? !!(window.tronLink || window.tronWeb) : !!window.ethereum);

/** The coin that pays network fees on a chain (product names, not translated). */
export const GAS_TOKEN: Record<string, string> = { tron: "TRX", bsc: "BNB", eth: "ETH", base: "ETH", arbitrum: "ETH", polygon: "POL" };
export const gasToken = (chain: string) => GAS_TOKEN[chain] ?? "gas";
/** Where to get a wallet for a chain. */
export const walletInstallUrl = (chain: string) => (isTron(chain) ? "https://www.tronlink.org/" : "https://metamask.io/download/");

const errMessage = (e: unknown) => String((e as { message?: string })?.message ?? e ?? "");
const isRejected = (e: unknown) => {
  const err = e as { code?: number | string };
  return err?.code === 4001 || err?.code === "ACTION_REJECTED" || /reject|denied|cancel/i.test(errMessage(e));
};
const isNoGas = (e: unknown) => /insufficient funds|insufficient balance|not enough|balance is not sufficient|bandwidth|energy/i.test(errMessage(e));
/** Map a wallet exception to a plain-language kind. */
const classify = (e: unknown) =>
  isRejected(e) ? new WalletError("rejected") : isNoGas(e) ? new WalletError("insufficient_gas", errMessage(e)) : new WalletError("failed", errMessage(e));

const tronWeb = () => window.tronLink?.tronWeb ?? window.tronWeb;

/**
 * Step 2: open the wallet, check the connected account is the vault owner and (EVM) switch to the vault's chain.
 * Returns the connected owner account. Throws WalletError (no_wallet, rejected, no_account, wrong_account, wrong_network).
 */
export async function connectOwner(call: PreparedCall): Promise<string> {
  if (isTron(call.chain)) {
    const link = window.tronLink;
    if (!link && !window.tronWeb) throw new WalletError("no_wallet");
    if (link?.request) {
      try {
        await link.request({ method: "tron_requestAccounts" });
      } catch (e) {
        if (isRejected(e)) throw new WalletError("rejected");
      }
    }
    const account = tronWeb()?.defaultAddress?.base58 || "";
    if (!account) throw new WalletError("no_account");
    if (account !== call.owner) throw new WalletError("wrong_account", "", account);
    return account;
  }

  const eth = window.ethereum;
  if (!eth) throw new WalletError("no_wallet");
  let accounts: string[];
  try {
    accounts = ((await eth.request({ method: "eth_requestAccounts" })) as string[]) ?? [];
  } catch (e) {
    throw isRejected(e) ? new WalletError("rejected") : new WalletError("failed", errMessage(e));
  }
  if (!accounts.length) throw new WalletError("no_account");
  const owner = accounts.find((a) => a.toLowerCase() === call.owner.toLowerCase());
  if (!owner) throw new WalletError("wrong_account", "", accounts[0]);
  if (call.chain_id !== null) {
    const want = `0x${call.chain_id.toString(16)}`;
    let current = "";
    try {
      current = String(await eth.request({ method: "eth_chainId" }));
    } catch {
      /* some wallets don't answer: just ask to switch */
    }
    if (current.toLowerCase() !== want) {
      try {
        await eth.request({ method: "wallet_switchEthereumChain", params: [{ chainId: want }] });
      } catch (e) {
        throw isRejected(e) ? new WalletError("rejected") : new WalletError("wrong_network", errMessage(e));
      }
    }
  }
  return owner;
}

/** Step 3: ask the wallet to sign and send the call. Returns the transaction hash / id. */
export async function sendCall(call: PreparedCall, from: string): Promise<string> {
  if (isTron(call.chain)) {
    const tw = tronWeb();
    if (!tw) throw new WalletError("no_wallet");
    try {
      const built = await tw.transactionBuilder.triggerSmartContract(
        call.to, call.function_selector, { feeLimit: 100_000_000, rawParameter: call.raw_parameter }, [], call.owner,
      );
      const signed = await tw.trx.sign(built.transaction);
      const res = await tw.trx.sendRawTransaction(signed);
      if (res.result === false) throw new Error(res.message || res.code || "broadcast failed");
      return res.txid ?? res.transaction?.txID ?? (signed as { txID?: string }).txID ?? "";
    } catch (e) {
      throw classify(e);
    }
  }
  const eth = window.ethereum;
  if (!eth) throw new WalletError("no_wallet");
  try {
    return (await eth.request({ method: "eth_sendTransaction", params: [{ from, to: call.to_hex, data: call.data }] })) as string;
  } catch (e) {
    throw classify(e);
  }
}

/** Explorer link for a transaction, derived from the vault's explorer link (same explorer and network). */
export function txExplorerUrl(vaultUrl: string | null, chain: string, hash: string): string | null {
  if (!vaultUrl || !hash) return null;
  if (isTron(chain)) return vaultUrl.replace(/#\/(contract|address)\/.*$/, `#/transaction/${hash}`);
  return vaultUrl.replace(/\/address\/[^/?#]+.*$/, `/tx/${hash}`);
}
